import React, { useState, useEffect } from 'react';

const VerificationsHistory = ({ record, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        
        // Determinar la URL de la API basada en el entorno
        const API_URL = process.env.NODE_ENV === 'production' 
          ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
          : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

        const response = await fetch(`${API_URL}/verifications/${record.id}/history`);
        if (!response.ok) {
          throw new Error('Error al cargar el historial');
        }
        const data = await response.json();
        setHistory(data);
      } catch (error) {
        console.error('Error fetching history:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (record?.id) {
      fetchHistory();
    }
  }, [record?.id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    const icons = {
      'verification_completed': '✅',
      'follow_up': '📞',
      'email_sent': '📧',
      'meeting_scheduled': '📅',
      'issue_found': '⚠️',
      'validation_passed': '🎯',
      'comment_added': '�',
      'status_changed': '🔄',
      'created': '✨',
      'updated': '📝',
      'action_added': '⚡',
      'completed': '✅',
      'cancelled': '❌',
      'quick_action': '🚀'
    };
    return icons[actionType] || '📋';
  };

  const getActionColor = (actionType) => {
    const colors = {
      'created': 'text-green-600 bg-green-50',
      'updated': 'text-blue-600 bg-blue-50',
      'status_changed': 'text-purple-600 bg-purple-50',
      'action_added': 'text-orange-600 bg-orange-50',
      'completed': 'text-green-600 bg-green-50',
      'cancelled': 'text-red-600 bg-red-50',
      'quick_action': 'text-indigo-600 bg-indigo-50'
    };
    return colors[actionType] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-600">Cargando historial...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                📋 Historial de Verificación
              </h2>
              <p className="text-purple-100 text-sm">
                Cliente: {record.nombre_cliente}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-purple-200 transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {error ? (
            <div className="text-center py-8">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar historial</h3>
              <p className="text-gray-500">{error}</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-6xl mb-4">📝</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Sin historial</h3>
              <p className="text-gray-500">No hay entradas en el historial para esta verificación.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Información del registro */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Información de la Verificación</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Cliente:</span>
                    <span className="ml-2 text-gray-900">{record.nombre_cliente}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Célula:</span>
                    <span className="ml-2 text-gray-900">{record.celula}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Campaña:</span>
                    <span className="ml-2 text-gray-900">{record.campana}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Estado:</span>
                    <span className="ml-2 text-gray-900">{record.status}</span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-700">Asunto:</span>
                    <span className="ml-2 text-gray-900">{record.asunto}</span>
                  </div>
                </div>
              </div>

              {/* Timeline del historial */}
              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-purple-200"></div>
                
                {history.map((entry, index) => (
                  <div key={entry.id} className="relative flex items-start space-x-4 pb-6">
                    {/* Icono de la acción */}
                    <div className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-2xl ${getActionColor(entry.action_type)}`}>
                      {getActionIcon(entry.action_type)}
                    </div>
                    
                    {/* Contenido de la entrada */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900 capitalize">
                            {entry.action_type.replace('_', ' ')}
                          </h4>
                          <span className="text-sm text-gray-500">
                            {formatDate(entry.created_at)}
                          </span>
                        </div>
                        
                        {entry.description && (
                          <p className="text-gray-700 mb-2">{entry.description}</p>
                        )}
                        
                        {entry.comment && (
                          <div className="bg-gray-50 rounded p-3 mb-2">
                            <p className="text-sm text-gray-600">{entry.comment}</p>
                          </div>
                        )}
                        
                        {entry.details && (
                          <div className="bg-blue-50 rounded p-3 mb-2">
                            <p className="text-sm text-blue-800">{entry.details}</p>
                          </div>
                        )}
                        
                        {(entry.old_status || entry.new_status) && (
                          <div className="flex items-center space-x-2 text-sm">
                            {entry.old_status && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 rounded">
                                {entry.old_status}
                              </span>
                            )}
                            {entry.old_status && entry.new_status && (
                              <span className="text-gray-400">→</span>
                            )}
                            {entry.new_status && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                                {entry.new_status}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {entry.user_name && (
                          <div className="mt-2 text-xs text-gray-500">
                            Por: {entry.user_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationsHistory;
